/**
 * Centralized API client for ecom-backend.
 *
 * Provides:
 * - Automatic Bearer token injection via getAuthHeaders()
 * - Consistent error handling with typed ApiError
 * - Basic retry logic for transient failures (network errors, 5xx)
 * - Structured request/response logging
 *
 * Usage:
 *   import { get, post, put } from '../services/apiClient';
 *   const res = await get('/api/cart', { cartId: '123' });
 */

import { appConfig } from '../../config/app-config';

import { getAuthHeaders } from './apiAuth';

// ==========================================
// Globals
// ==========================================
let globalStoreKey: string | undefined;

export function setGlobalStoreKey(key: string | undefined): void {
  globalStoreKey = key;
}

export function getGlobalStoreKey(): string | undefined {
  return globalStoreKey;
}

// ==========================================
// Types
// ==========================================

/** Configuration for the API client */
interface ApiClientConfig {
  baseUrl: string;
  defaultHeaders: Record<string, string>;
  retry: RetryConfig;
}

/** Retry configuration */
interface RetryConfig {
  maxRetries: number;
  baseDelayMs: number;
  retryableStatusCodes: number[];
}

/** Options for individual requests */
interface RequestOptions {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  headers?: Record<string, string>;
  body?: unknown;
  searchParams?: Record<string, string | string[]>;
  /** Override default retry config for this request */
  retry?: Partial<RetryConfig>;
  /** Skip auth header injection (e.g. for public endpoints) */
  skipAuth?: boolean;
}

// ==========================================
// ApiError Class
// ==========================================

/** Typed error for API failures with HTTP context */
export class ApiError extends Error {
  public readonly statusCode?: number;
  public readonly errorCode: string;
  public readonly responseBody?: string;

  constructor(
    message: string,
    errorCode: string,
    statusCode?: number,
    responseBody?: string
  ) {
    super(message);
    this.name = 'ApiError';
    this.errorCode = errorCode;
    this.statusCode = statusCode;
    this.responseBody = responseBody;
  }
}

// ==========================================
// Constants
// ==========================================

const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxRetries: 2,
  baseDelayMs: 1000,
  retryableStatusCodes: [408, 429, 500, 502, 503, 504],
};

// ==========================================
// Helpers
// ==========================================

/** Check if a status code is retriable */
function isRetriableStatus(status: number, config: RetryConfig): boolean {
  return config.retryableStatusCodes.includes(status);
}

/** Wait for a given number of milliseconds */
function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/** Get the base URL from app config */
function getBaseUrl(): string {
  return appConfig.backend.baseUrl;
}

/** Build a full URL with optional search params */
function buildUrl(
  path: string,
  searchParams?: Record<string, string | string[]>
): string {
  const base = getBaseUrl();
  const url = new URL(path, base);

  if (searchParams) {
    Object.entries(searchParams).forEach(([key, value]) => {
      if (value == null || value === '') return;
      const values = Array.isArray(value) ? value : [value];
      values.forEach(v => url.searchParams.append(key, v));
    });
  }

  return url.toString();
}

// ==========================================
// Core Request Function
// ==========================================

/**
 * Execute an HTTP request with auth headers, retry logic, and error handling.
 *
 * @param path - API path (e.g. '/api/cart')
 * @param options - Request options
 * @returns The raw fetch Response
 */
async function request(path: string, options: RequestOptions): Promise<Response> {
  const retryConfig: RetryConfig = {
    ...DEFAULT_RETRY_CONFIG,
    ...(options.retry || {}),
  };

  const url = buildUrl(path, options.searchParams);

  // Endpoints that should not include the Authorization header
  const PUBLIC_ENDPOINTS = appConfig.backend.publicEndpoints;

  const isPublicEndpoint = PUBLIC_ENDPOINTS.some(ep =>
    path && path.startsWith(ep)
  );
  // Build headers: start with auth headers, then merge custom ones
  const rawHeaders: Record<string, string> = {
    ...(options.skipAuth || isPublicEndpoint
      ? { 'Content-Type': 'application/json' }
      : await getAuthHeaders()),
    ...options.headers,
  };

  if (globalStoreKey && !rawHeaders['x-store-key'] && !rawHeaders['X-Store-Key']) {
    rawHeaders['x-store-key'] = globalStoreKey;
  }

  // Normalize all headers to lowercase and strip authorization for public endpoints
  const headers: Record<string, string> = {};
  for (const [key, value] of Object.entries(rawHeaders)) {
    const normalizedKey = key.toLowerCase();
    if ((options.skipAuth || isPublicEndpoint) && normalizedKey === 'authorization') {
      continue;
    }
    headers[normalizedKey] = value;
  }

  // Build fetch init
  const fetchInit: RequestInit = {
    method: options.method,
    headers,
  };

  if (options.body != null) {
    fetchInit.body = JSON.stringify(options.body);
  }

  // Log outgoing request


  let lastError: Error | null = null;

  for (let attempt = 0; attempt <= retryConfig.maxRetries; attempt++) {
    try {
      const response = await fetch(url, fetchInit);

      // Success — return immediately
      if (response.ok) {
        return response;
      }

      // Non-retriable HTTP error — fail immediately
      if (!isRetriableStatus(response.status, retryConfig)) {
        let errorBody: string;
        try {
          errorBody = await response.text();
        } catch {
          errorBody = 'Unable to read error response';
        }



        throw new ApiError(
          `HTTP ${response.status}: ${errorBody}`,
          'HTTP_ERROR',
          response.status,
          errorBody
        );
      }

      // Retriable HTTP error
      let errorBody: string;
      try {
        errorBody = await response.text();
      } catch {
        errorBody = 'Unable to read error response';
      }

      lastError = new ApiError(
        `HTTP ${response.status}: ${errorBody}`,
        'HTTP_RETRIABLE_ERROR',
        response.status,
        errorBody
      );

      if (attempt < retryConfig.maxRetries) {
        const waitMs = retryConfig.baseDelayMs * Math.pow(2, attempt);

        await delay(waitMs);
      }
    } catch (error: unknown) {
      // If it's already an ApiError with a non-retriable status, re-throw
      if (error instanceof ApiError && error.statusCode && !isRetriableStatus(error.statusCode, retryConfig)) {
        throw error;
      }

      // Network error — retriable
      const message = error instanceof Error ? error.message : String(error);
      lastError = new ApiError(
        `Network error: ${message}`,
        'NETWORK_ERROR'
      );

      if (attempt < retryConfig.maxRetries) {
        const waitMs = retryConfig.baseDelayMs * Math.pow(2, attempt);

        await delay(waitMs);
      }
    }
  }

  // All retries exhausted
  throw lastError!;
}

// ==========================================
// Public API
// ==========================================

/**
 * GET request with optional query parameters.
 *
 * @param path - e.g. '/api/cart' or '/api/orders/123'
 * @param searchParams - optional query params (arrays appended multiple times)
 * @returns Promise<Response>
 */
export async function get(
  path: string,
  searchParams: Record<string, string | string[]> = {},
  options?: Omit<RequestOptions, 'method' | 'searchParams'>
): Promise<Response> {
  return request(path, {
    method: 'GET',
    searchParams,
    ...options,
  });
}

/**
 * POST request with optional JSON body.
 *
 * @param path - API path
 * @param body - optional request body (will be JSON.stringify'd)
 * @param options - optional request options
 * @returns Promise<Response>
 */
export async function post(
  path: string,
  body?: unknown,
  options?: Omit<RequestOptions, 'method' | 'body'>
): Promise<Response> {
  return request(path, {
    method: 'POST',
    body,
    ...options,
  });
}

/**
 * PUT request with optional JSON body.
 *
 * @param path - API path
 * @param body - optional request body (will be JSON.stringify'd)
 * @param options - optional request options
 * @returns Promise<Response>
 */
export async function put(
  path: string,
  body?: unknown,
  options?: Omit<RequestOptions, 'method' | 'body'>
): Promise<Response> {
  return request(path, {
    method: 'PUT',
    body,
    ...options,
  });
}

/**
 * Get base URL for cases where URL is built manually.
 */
export function getApiBaseUrl(): string {
  return getBaseUrl();
}

// ==========================================
// Advanced API (for consumers needing more control)
// ==========================================

/**
 * Low-level request function for advanced use cases.
 * Allows full control over method, headers, retry, and auth.
 */
export { request };

// Export types for consumers
export type { ApiClientConfig, RetryConfig, RequestOptions };
